<template>
  <div id="resource-settings" v-show="!loading">
    <v-container class="content-wrapper" fluid>
      <v-row justify="center" align="center">
        <v-col cols="12" lg="10">
          <v-card
            class="pa-3 pa-sm-6 pa-lg-6 pa-md-6 pa-xl-6"
            elevation="3"
            style="border-radius: 24px;"
          >
            <v-row align="center" class="mb-10">
              <v-col cols="12" md="auto">
                <v-btn
                  @click="backToServices"
                  depressed
                  rounded
                  medium
                  color="primary200"
                  class="white--text font-weight-bold"
                >
                  <v-icon color="black" small class="mr-2">
                    mdi-arrow-left
                  </v-icon>
                  Back to your Services
                </v-btn>
              </v-col>
              <v-spacer></v-spacer>
              <v-col cols="12" md="6" class="justify-end d-flex pt-5 ">
                <v-btn-toggle v-model="toggle_visibility" rounded mandatory>
                  <v-tooltip bottom color="neutral">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn v-bind="attrs" v-on="on" value="published" @click="publishResource()">
                        {{ $t('public') }}
                        <v-icon class="pl-2">mdi-information-outline</v-icon>
                      </v-btn>
                    </template>
                    <span> {{ $t('tooltip-public') }}</span>
                  </v-tooltip>
                  <v-tooltip bottom color="neutral">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        v-bind="attrs"
                        v-on="on"
                        value="unpublished"
                        @click="unpublishResource()"
                      >
                        {{ $t('private') }}
                        <v-icon class="pl-2">mdi-information-outline</v-icon>
                      </v-btn>
                    </template>
                    <span> {{ $t('tooltip-private') }}</span>
                  </v-tooltip>
                </v-btn-toggle>
              </v-col>
            </v-row>
            <v-row>
              <v-col
                v-if="resource.draft"
                cols="12"
                md="6"
                class="neutral--text text-semi-bold mb-4"
              >
                {{ $t('add-resource-info') }}
              </v-col>
            </v-row>
            <div v-if="resource.draft">
              <h5>
                {{ $t('your-progress') }}
              </h5>
              <label class="text-bold primary300--text"
                >{{ tab + 1 }}/{{ totalTabs }} {{ $t('to-complete') }}</label
              >
              <v-progress-linear
                v-model="progress"
                color="primary300"
                id="provider-progress-bar"
                class="mb-4 mt-4"
                height="25"
              ></v-progress-linear>
            </div>

            <v-tabs
              v-model="tab"
              :class="{ 'disable-transition': resource.draft }"
              show-arrows
              active-class="primary200--text"
            >
              <v-tabs-slider color="primary200"></v-tabs-slider>
              <v-tab>{{ $t('basic') }}</v-tab>
              <v-tab>{{ $t('marketing') }}</v-tab>
              <v-tab>{{ $t('classification') }}</v-tab>
              <v-tab>{{ $t('contact') }}</v-tab>
              <v-tab v-show="isEquipment">{{ $t('equipment') }}</v-tab>
              <v-tab v-show="!resource.draft">{{ $t('management') }}</v-tab>
              <v-tab v-show="!resource.draft">{{ $t('pricing') }}</v-tab>
              <v-tab v-show="!resource.draft">{{ $t('additional-information') }}</v-tab>
            </v-tabs>
            <v-row class="mt-2">
              <v-col cols="12" class="d-flex justify-end">
                <v-btn
                  depressed
                  large
                  rounded
                  class="white--text font-weight-bold"
                  color="primary200"
                  @click="edit = true"
                  v-if="!resource.draft && !edit"
                >
                  <v-icon>mdi-pencil-outline</v-icon>
                  {{ $t('edit') }}
                </v-btn>
              </v-col>
            </v-row>
            <v-tabs-items v-model="tab">
              <v-tab-item :transition="false" :reverse-transition="false" :key="0">
                <v-row>
                  <v-col cols="12">
                    <resource-basic-info
                      :admin="admin"
                      :id="id"
                      :resourceVisibility="toggle_visibility"
                      :next="resource.draft"
                      :edit="resource.draft || edit"
                      :countOrders="countOrders"
                      @cancel="edit = false"
                      @tab="changeTab"
                      @resourceId="resourceId"
                    ></resource-basic-info>
                  </v-col>
                </v-row>
              </v-tab-item>
              <v-tab-item :transition="false" :reverse-transition="false" :key="1">
                <v-row>
                  <v-col cols="12">
                    <resource-marketing-info
                      :admin="admin"
                      :id="id"
                      :next="resource.draft"
                      :edit="resource.draft || edit"
                      @cancel="edit = false"
                      @tab="changeTab"
                    ></resource-marketing-info>
                  </v-col>
                </v-row>
              </v-tab-item>
              <v-tab-item :transition="false" :reverse-transition="false" :key="2">
                <v-row>
                  <v-col cols="12">
                    <resource-classification-info
                      :admin="admin"
                      :id="id"
                      :next="resource.draft"
                      :edit="resource.draft || edit"
                      @cancel="edit = false"
                      @tab="changeTab"
                      @isEquipment="isEquipmentHandler"
                    ></resource-classification-info>
                  </v-col>
                </v-row>
              </v-tab-item>
              <v-tab-item :transition="false" :reverse-transition="false" :key="3">
                <v-row>
                  <v-col cols="12">
                    <resource-contact-info
                      :admin="admin"
                      :id="id"
                      :isEquipment="isEquipment"
                      :next="resource.draft"
                      :edit="resource.draft || edit"
                      @cancel="edit = false"
                      @tab="changeTab"
                    ></resource-contact-info>
                  </v-col>
                </v-row>
              </v-tab-item>
              <v-tab-item :transition="false" :reverse-transition="false" :key="4">
                <v-row>
                  <v-col cols="12">
                    <resource-equipment-info
                      :admin="admin"
                      :id="id"
                      :next="resource.draft"
                      :edit="resource.draft || edit"
                      @cancel="edit = false"
                      @tab="changeTab"
                    ></resource-equipment-info>
                  </v-col>
                </v-row>
              </v-tab-item>
              <v-tab-item :transition="false" :reverse-transition="false" :key="5">
                <v-row>
                  <v-col cols="12">
                    <resource-management-info
                      :admin="admin"
                      :id="id"
                      :next="false"
                      :edit="edit"
                      @cancel="edit = false"
                    ></resource-management-info>
                  </v-col>
                </v-row>
              </v-tab-item>
              <v-tab-item :transition="false" :reverse-transition="false" :key="6">
                <v-row>
                  <v-col cols="12">
                    <ResourcePricingInfo
                      :admin="admin"
                      :id="id"
                      :next="false"
                      :edit="edit"
                      @cancel="edit = false"
                    ></ResourcePricingInfo>
                  </v-col>
                </v-row>
              </v-tab-item>
              <v-tab-item :transition="false" :reverse-transition="false" :key="7">
                <v-row>
                  <v-col cols="12">
                    <ResourceAdditionalInfo
                      :admin="admin"
                      :id="id"
                      :next="false"
                      :edit="edit"
                      @cancel="edit = false"
                    ></ResourceAdditionalInfo>
                  </v-col>
                </v-row>
              </v-tab-item>
            </v-tabs-items>
          </v-card>
        </v-col>
      </v-row>

      <!-- Draft Dialog -->
      <v-dialog v-model="draftDialog" max-width="1000px" persistent>
        <v-card
          class="py-4 px-5 px-sm-16 px-md-16 px-lg-16 px-xl-16 py-sm-8 py-md-8 py-lg-8 py-xl-8"
        >
          <v-card-title class="px-0 py-0">
            <v-row class="mx-0 px-2" align="center">
              <v-col cols="11" class="justify-center d-flex">
                <img
                  alt="Logo"
                  width="180"
                  class="rounded-0 text-center"
                  :src="require('@/assets/svg/logo.svg')"
                />
              </v-col>
              <v-col cols="1" class="pl-0">
                <v-btn fab small text @click="draftDialog = false">
                  <v-icon color="neutral500">
                    mdi-close
                  </v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </v-card-title>

          <v-divider class="custom-divider mt-10"></v-divider>

          <v-card-text class="justify-center d-flex mt-2">
            <v-icon size="150" color="black">$vuetify.icons.errorCirle</v-icon>
          </v-card-text>
          <v-card-text
            class="justify-center d-flex mt-2 black--text font-weight-bold"
            style="font-size: 20px;"
          >
            <div>
              Leave Site?
            </div>
          </v-card-text>
          <v-card-text class="justify-center d-flex mt-2">
            <div>
              Changes you made may not be saved.
            </div>
          </v-card-text>
          <v-card-actions>
            <v-row style="width: 100%">
              <v-col cols="6">
                <v-btn
                  @click="draftDialog = false"
                  block
                  depressed
                  rounded
                  large
                  class="neutral800--text font-weight-bold"
                  color="neutral"
                  outlined
                  >{{ $t('cancel') }}
                </v-btn>
              </v-col>
              <v-col cols="6">
                <v-btn
                  :to="{ name: 'Services' }"
                  block
                  depressed
                  rounded
                  large
                  class="white--text font-weight-bold"
                  color="black"
                  >{{ $t('leave') }}
                </v-btn>
              </v-col>
            </v-row>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </div>
</template>

<script>
import util from '@/mixins/util.js';
import ResourceClassificationInfo from '@/components/add-resource/ResourceClassificationInfo';
import ResourceContactInfo from '@/components/add-resource/ResourceContactInfo';
import ResourceMarketingInfo from '@/components/add-resource/ResourceMarketingInfo';
import ResourceBasicInfo from '@/components/add-resource/ResourceBasicInfo';
import EventBus from '@/helpers/event-bus';
import ResourceEquipmentInfo from '@/components/add-resource/ResourceEquipmentInfo';
import ResourceManagementInfo from '@/components/add-resource/ResourceManagementInfo';
import ResourcePricingInfo from './ResourcePricingInfo.vue';
import ResourceAdditionalInfo from './ResourceAdditionalInfo.vue';

export default {
  name: 'ServiceSettings',
  components: {
    ResourceManagementInfo,
    ResourceEquipmentInfo,
    ResourceClassificationInfo,
    ResourceContactInfo,
    ResourceMarketingInfo,
    ResourceBasicInfo,
    ResourcePricingInfo,
    ResourceAdditionalInfo,
  },
  mixins: [util],
  created() {
    if (this.isFirstTime() && this.$store.getters.roles !== 'super-admin') {
      const marketplaceServicesViewAll = true;
      const selectedCatalogue = 'Browse the Catalogue of Services';
      let routePath = '';
      routePath = '/marketplace/services';
      this.$router.push({
        path: routePath,
        query: { selectedCatalogue, marketplaceServicesViewAll },
      });
    } else if (this.$store.getters.roles === 'super-admin') {
      this.edit = true;
      this.admin = true;
    }
  },
  data() {
    return {
      // breadcrumbs: this.$router.currentRoute.meta.breadcrumbPath,
      id: null,
      tab: null,
      edit: false,
      admin: false,
      resource: {
        draft: true,
        published: true,
      },
      toggle_visibility: 'published',
      isEquipment: false,
      loading: true,
      countOrders: null,
      draftDialog: false,
    };
  },
  mounted() {
    let id = this.$route.query.id;
    if (id != null) {
      this.id = id;
      EventBus.$emit('waiting', true);
      this.axios
        .get('/api/v1/resources/' + this.id)
        .then(res => {
          this.resource = res.data;
          // this.$set(this.breadcrumbs, this.breadcrumbs.length - 1, { text: this.resource.name });
          if (!this.resource.published) {
            this.toggle_visibility = 'unpublished';
          }
          if (
            this.resource.categories.filter(e => e.category === 'Instruments & Equipment').length >
            0
          ) {
            this.isEquipment = true;
          } else {
            this.isEquipment = false;
          }
          this.$nextTick(() => {
            this.loading = false;
          });
        })
        .catch(error => {
          console.log({ error });
        })
        .finally(() => {
          EventBus.$emit('waiting', false);
        });
      if (this.admin) {
        this.axios
          .get('/api/v1/orders/count-orders/' + this.id)
          .then(res => {
            console.log('count orders');
            console.log(res.data);
            this.countOrders = res.data;
          })
          .catch(error => {
            console.log({ error });
          })
          .finally(() => {
            EventBus.$emit('waiting', false);
          });
      }
    } else {
      this.loading = false;
    }
  },
  methods: {
    changeTab(payload) {
      console.log('[Tab] Tab changed');
      if (this.resource.draft) {
        this.tab = payload;
      }
    },
    resourceId(value) {
      this.id = value;
    },
    isEquipmentHandler(value) {
      console.log('Is equipment ' + value);
      this.isEquipment = value;
    },
    publishResource() {
      if (this.id != null) {
        EventBus.$emit('waiting', true);
        this.axios
          .post('/api/v1/resources/' + this.id + '/publish')
          .then(() => {
            EventBus.$emit('snackbar', {
              text: 'Visibility changed to published',
            });
          })
          .catch(error => {
            console.log({ error });
          })
          .finally(() => {
            EventBus.$emit('waiting', false);
          });
      }
    },
    unpublishResource() {
      if (this.id != null) {
        EventBus.$emit('waiting', true);
        this.axios
          .post('/api/v1/resources/' + this.id + '/unpublish')
          .then(() => {
            EventBus.$emit('snackbar', {
              text: 'Visibility changed to private',
            });
          })
          .catch(error => {
            console.log({ error });
          })
          .finally(() => {
            EventBus.$emit('waiting', false);
          });
      }
    },
    backToServices() {
      if (this.resource.draft) {
        this.draftDialog = true;
      } else if (
        !this.resource.draft &&
        this.$store.getters.roles === 'provider' &&
        this.$route.query.fromTable
      ) {
        const fromTable = this.$route.query;
        this.$router.push({
          name: 'Services',
          query: fromTable,
        });
      } else if (
        !this.resource.draft &&
        this.$store.getters.roles === 'super-admin' &&
        this.$route.query.fromTable
      ) {
        const fromTable = this.$route.query;
        this.$router.push({
          name: 'AdminServices',
          query: fromTable,
        });
      }
    },
  },
  computed: {
    totalTabs() {
      return this.isEquipment ? 5 : 4;
    },
    progress() {
      return ((this.tab + 1) / this.totalTabs) * 100;
    },
  },
};
</script>

<style scoped>
.custom-divider {
  padding-top: 5px;
  margin: 0 35px;
  border-top-width: 2px;
  border-top-style: solid;
  border-top-color: rgba(195, 194, 199, 1);
}
</style>
